import { Container, CreateProductionRouteDialog, LoadingScreen, ToDo } from '@/shared/components'
import { useInstallation } from '@/features/installations/hooks/useInstallations'
import { useAppState } from '@/app/hooks/useAppState'
import { useSession } from '@/shared/hooks/useSession'
import InstallationCard from './components/InstallationCard'
import { useEffect } from 'react'
import { Navigate, useNavigate, useParams } from 'react-router-dom'
import { useGetProductionRoutesForInstallation } from '@/hooks/api/v2/productionRoutes'
import useI18n from '@/shared/hooks/useI18n'
import { Button, H2, PageSection } from '@/common'
import ProductionRouteListItem from './components/ProductionRouteListItem'
import useDialog from '@/common/hooks/useDialog'
import { PencilIcon, PlusIcon } from '@/common/components/icons'
import { usePermissions } from '@/shared/hooks/usePermissions'

export default function InstallationPage() {
  const { t } = useI18n()
  const navigate = useNavigate()
  const installationId = useParams<{ installationId: string }>().installationId!
  const { setSelectedInstallationId } = useAppState()
  const { checkPermission } = usePermissions()
  const canEditInstallation = checkPermission('installation', 'update')

  useEffect(() => {
    setSelectedInstallationId(installationId)
  }, [installationId, setSelectedInstallationId])

  const {
    open: isProductionRouteCreationDialogOpen,
    openDialog: openProductionRouteCreationDialog,
    closeDialog: closeProductionRouteCreationDialog
  } = useDialog()

  const organization = useSession().session.organization
  const { data: installation, isLoading } = useInstallation(organization.id, installationId!)
  const productionRoutesQuery = useGetProductionRoutesForInstallation(installationId)

  const productionRoutes =
    productionRoutesQuery.data?.filter(productionRoute => !productionRoute.deletedAt) ?? []

  if (isLoading) {
    return <LoadingScreen />
  }

  if (!installation) {
    return <Navigate to="/installation-setup" />
  }

  const installationReportingPeriods = installation.reportingPeriods.map(period => ({
    startDate: new Date(period.startDate),
    endDate: new Date(period.endDate)
  }))
  const hasProductionRoutes = productionRoutes.length > 0

  const reportingSetupSteps = [
    {
      id: 'create-production-route',
      title: t('dashboard:reporting.reportingSetup.steps.createProductionRoute.title'),
      content: t('dashboard:reporting.reportingSetup.steps.createProductionRoute.content'),
      isCompleted: hasProductionRoutes || false,
      actionButton: t(
        'dashboard:reporting.reportingSetup.steps.createProductionRoute.actionButton'
      ),
      action: () => {
        openProductionRouteCreationDialog()
      }
    }
  ]

  return (
    <>
      <Container>
        <header>
          <H2>{installation.name}</H2>
        </header>
        <PageSection
          title={t('dashboard:details.title')}
          actions={
            <Button
              className="flex items-center"
              sizeType="sm"
              variant="subtle"
              icon={<PencilIcon />}
              disabled={!canEditInstallation}
              onClick={() => navigate(`/dashboard/${installationId}/edit`)}
            >
              {t('common:actions.edit')}
            </Button>
          }
        >
          <InstallationCard installation={installation} />
        </PageSection>
        <PageSection
          title={t('dashboard:reporting.title')}
          actions={
            hasProductionRoutes && (
              <Button
                icon={<PlusIcon />}
                sizeType="sm"
                variant="subtle"
                onClick={() => openProductionRouteCreationDialog()}
              >
                {t('dashboard:reporting.addProductionRouteButton')}
              </Button>
            )
          }
        >
          {!hasProductionRoutes ? (
            <ToDo
              title={t('dashboard:reporting.reportingSetup.title')}
              items={reportingSetupSteps}
            />
          ) : (
            <ul className="space-y-1">
              {productionRoutes.map(productionRoute => (
                <ProductionRouteListItem
                  key={productionRoute.id}
                  reportingPeriodBasis={installation.basisForReportingPeriod}
                  installationId={installationId}
                  id={productionRoute.id}
                  name={productionRoute.name}
                  productionMethods={productionRoute.productionMethods}
                  reportingPeriods={installationReportingPeriods}
                  pivotDate={installation.createdAt ? new Date(installation.createdAt) : new Date()}
                />
              ))}
            </ul>
          )}
        </PageSection>
      </Container>
      <CreateProductionRouteDialog
        isOpen={isProductionRouteCreationDialogOpen}
        close={closeProductionRouteCreationDialog}
        installationId={installationId}
      />
    </>
  )
}
